import { computed, ref, watch } from "vue";

import { maxCoord, minCoord, minus, plus, times, toSize } from "@/math/coordinates";
import type { IdMap } from "@/model/baseTypes";
import type { BoardCard } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import { halfCardSize, useBoardStore } from "@/store/board";
import type { Drag } from "@/store/dragging";
import { useDraggingStore } from "@/store/dragging";

const PADDING = relativeCoord(-0.5, -0.5);

export function useStickyBoundingRect(width: number, height: number) {
  const pos = ref<RelativeCoordinate[]>([]);
  watch(
    selectionAndDrag,
    ([drag, selection]) => {
      pos.value = dragPos(drag) ?? selectionPos(selection) ?? [];
    },
    { immediate: true, deep: true },
  );

  function selectionAndDrag(): [IdMap<Drag>, BoardCard[]] {
    return [useDraggingStore().dragging, useBoardStore().selectedStickies];
  }

  function dragPos(drag: IdMap<Drag>) {
    const drags = Object.values(drag);
    if (drags.length > 1 && drags[0].type === "card") {
      return drags.map((d) => d.pos);
    }
  }

  function selectionPos(selection: BoardCard[]) {
    if (selection.length > 1) {
      return selection.map((card) => ({ ...card.meta.pos }));
    }
  }

  const boundingRect = computed(() => {
    if (pos.value.length === 0) {
      return;
    }
    const size = halfCardSize();
    const start = minus(minCoord(pos.value), size);
    const end = plus(maxCoord(pos.value), size);
    const scaledStart = times(start, width, height);
    const scaledSize = times(minus(end, start), width, height);

    const baseRect = {
      ...minus(scaledStart, PADDING),
      ...toSize(plus(scaledSize, times(PADDING, 2))),
    };

    // Extra margin which will cover the stiky note outline too
    const extraMargin = 0.4;

    return {
      x: baseRect.x - extraMargin,
      y: baseRect.y - extraMargin,
      width: baseRect.width + extraMargin * 2,
      height: baseRect.height + extraMargin * 2,
    };
  });

  return { boundingRect };
}
